import React, { useState } from "react";
import emailjs from "emailjs-com";
import { FiCheck } from "react-icons/fi";
import { openNirkanaWhatsApp } from "../lib/whatsapp";

const buildWhatsAppMessage = (data) =>
  `New vendor interest from the website:\n` +
  `Business: ${data.businessName}\n` +
  `Contact: ${data.contactName}\n` +
  `Phone: ${data.phone}\n` +
  `Category: ${data.category}` +
  (data.message ? `\nNotes: ${data.message}` : "");

const ondcPoints = [
  "Discoverable through the ONDC network alongside our own app and website",
  "Open, interoperable commerce infrastructure rather than a single closed platform",
  "The same trust and settlement protections ONDC brings to other categories",
];

const Partners = () => {
  const [formData, setFormData] = useState({ businessName: "", contactName: "", phone: "", category: "", message: "" });
  const [status, setStatus] = useState(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");

    // Fires synchronously, before any await, so browsers don't block it as a popup.
    openNirkanaWhatsApp(buildWhatsAppMessage(formData));

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_VENDOR_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (serviceId && templateId && publicKey) {
      try {
        await emailjs.send(serviceId, templateId, formData, publicKey);
      } catch (err) {
        console.warn("Vendor interest email failed to send:", err);
      }
    } else {
      console.warn("EmailJS env vars not set — vendor interest only sent via WhatsApp.");
    }

    setStatus("success");
    setFormData({ businessName: "", contactName: "", phone: "", category: "", message: "" });
  };

  return (
    <>
      <section className="bg-cloud py-16 lg:py-24">
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center rounded-full bg-gold-soft px-3 py-1 text-xs font-semibold text-gold">
            ONDC network partner
          </span>
          <h1 className="mt-4 max-w-prose text-3xl font-bold text-brand sm:text-4xl">
            Built on open network commerce
          </h1>
          <p className="mt-4 max-w-prose text-muted">
            Nirkana operates as a partner on the Open Network for Digital
            Commerce (ONDC), so the events and services we coordinate can be
            discovered and booked through a wider, open network rather than
            being locked into a single app.
          </p>

          <ul className="mt-8 max-w-prose space-y-3">
            {ondcPoints.map((point) => (
              <li key={point} className="flex items-start gap-3 text-sm text-charcoal">
                <FiCheck className="mt-0.5 shrink-0 text-action" size={16} />
                {point}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-paper py-16 lg:py-24">
        <div className="max-w-content mx-auto grid grid-cols-1 gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10">
          <div>
            <h2 className="text-3xl font-bold text-brand sm:text-4xl">Become a vendor</h2>
            <p className="mt-4 max-w-prose text-muted">
              If you offer decor, catering, photography, entertainment, or
              venue services, register your interest here. We're onboarding
              partners for the birthday launch now, with a dedicated vendor
              app for managing bookings and payouts planned next.
            </p>
            <div className="mt-8 space-y-3 text-sm text-muted">
              <p>Currently onboarding: Chennai and surrounding areas.</p>
              <p>No fees to register interest — we'll reach out to qualify partners individually.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="rounded-2xl bg-cloud p-6 sm:p-8">
            {status === "success" && (
              <div className="mb-5 rounded-lg bg-success/10 px-4 py-3 text-sm text-charcoal">
                Thanks — WhatsApp should have opened with your details ready
                to send. Tap send there and we'll follow up shortly.
              </div>
            )}
            <div className="space-y-4">
              <input type="text" name="businessName" required placeholder="Business name *" value={formData.businessName} onChange={handleChange} className="w-full rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action" />
              <input type="text" name="contactName" required placeholder="Contact person *" value={formData.contactName} onChange={handleChange} className="w-full rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action" />
              <input type="tel" name="phone" required placeholder="Phone number *" value={formData.phone} onChange={handleChange} className="w-full rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action" />
              <input type="text" name="category" required placeholder="Service category (decor, catering, etc.) *" value={formData.category} onChange={handleChange} className="w-full rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action" />
              <textarea name="message" rows="3" placeholder="Anything else we should know? (optional)" value={formData.message} onChange={handleChange} className="w-full rounded-lg border border-line px-4 py-3 text-charcoal placeholder:text-muted focus:border-action" />
            </div>
            <button type="submit" disabled={status === "sending"} className="mt-6 w-full rounded-full bg-brand px-6 py-3.5 text-base font-medium text-paper hover:bg-brand/90 transition-colors disabled:opacity-60">
              {status === "sending" ? "Sending..." : "Register interest"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Partners;
